// @flow

import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
  SchemaForm,
  TooltipDropdown,
} from "@components";
import type { ReportingParametersData } from "@modules/service/reducer/reporting-parameters/types";
import actionCreators from "@modules/reporting/actionCreator";
import type { Profile } from "@modules/account/types";
import type { ScheduledReportData } from "@modules/reporting/reducer/types";

type OwnProps = $Exact<{
  onToggle: Function,
  onSubmit: Function,
  reportingParameters: ReportingParametersData,
  userProfile: Profile,
  scheduleReportSettingsModalData: ScheduledReportData,
  reportName: string,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class ScheduleReportSettingsModal extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }

  submit = () => {
    this.formRef.current.submit();
  };

  onSubmit = (formData: Object) => {
    const {
      dayMonthly,
      daysWeekly,
      numberOfDaysDaily,
      numberOfMonthMonthly,
      numberOfWeeksWeekly,
      scheduledReportId,
      scheduledType,
      status,
    } = formData;
    const savedReportId = this.data.savedReportId;
    const fields = {
      DAILY: {
        status,
        scheduledType,
        savedReportId,
        scheduledReportId,

        numberOfDaysDaily,
      },
      WEEKLY: {
        status,
        scheduledType,
        savedReportId,
        scheduledReportId,

        numberOfWeeksWeekly,
        daysWeekly,
      },
      MONTHLY: {
        status,
        scheduledType,
        savedReportId,
        scheduledReportId,

        numberOfMonthMonthly,
        dayMonthly,
      },
    };
    let data = fields[scheduledType];
    if (!status)
      data = {
        savedReportId,
        scheduledReportId,
        scheduledType: "MONTHLY",
        numberOfMonthMonthly: 1,
        dayMonthly: 1,
        status: false,
      };

    this.props.onSubmit({ formData: data });
  };

  get data() {
    return this.props.scheduleReportSettingsModalData;
  }
  get schema() {
    return {
      title: "Person",
      type: "object",
      properties: {
        status: {
          title: "Would you like to schedule this report ?",
          type: "boolean",
          default: false,
        },
      },
      dependencies: {
        status: {
          oneOf: [
            {
              properties: {
                status: { enum: [false] },
              },
            },
            {
              properties: {
                status: { enum: [true] },
                scheduledType: {
                  title: "How often would you like to receive the report",
                  type: "string",
                  enum: ["DAILY", "WEEKLY", "MONTHLY"],
                  default: "Daily",
                },
              },
              dependencies: {
                scheduledType: {
                  oneOf: [
                    // Daily
                    {
                      properties: {
                        scheduledType: { enum: ["DAILY"] },
                        numberOfDaysDaily: {
                          title: "How frequently would you like to receive it?",
                          type: "number",
                          minimum: 1,
                        },
                      },
                      required: ["scheduledType", "numberOfDaysDaily"],
                    },
                    //Weekly
                    {
                      properties: {
                        scheduledType: { enum: ["WEEKLY"] },
                        numberOfWeeksWeekly: {
                          title: "How frequently would you like to receive it?",
                          type: "number",
                          minimum: 1,
                          maximum: 4,
                        },
                        daysWeekly: {
                          type: "array",
                          minItems: 1,
                          uniqueItems: true,
                          items: {
                            enum: [
                              "MONDAY",
                              "TUESDAY",
                              "WEDNESDAY",
                              "THURSDAY",
                              "FRIDAY",
                              "SATURDAY",
                              "SUNDAY",
                            ],
                          },
                        },
                      },
                      required: ["scheduledType", "numberOfWeeksWeekly", "daysWeekly"],
                    },
                    //Monthly
                    {
                      properties: {
                        scheduledType: { enum: ["MONTHLY"] },
                        numberOfMonthMonthly: {
                          title: "How frequently would you like to receive it?",
                          type: "number",
                          minimum: 1,
                          maximum: 12,
                        },
                        dayMonthly: {
                          title: "On which day of the month should it be sent?",
                          type: "number",
                          minimum: 1,
                          maximum: 31,
                        },
                      },
                      required: ["scheduledType", "numberOfMonthMonthly", "dayMonthly"],
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    };
  }
  get uiSchema() {
    return {
      status: {
        "ui:widget": "toggle",
        "ui:options": {
          labelposition: "left",
        },
      },
      scheduledType: {
        "ui:options": {
          labelposition: "left",
        },
      },
      numberOfDaysDaily: {
        "ui:widget": "number-input",
        "ui:options": {
          labelposition: "left",
        },
      },

      daysWeekly: {
        "ui:widget": "checkboxes",
        "ui:options": {
          label: "On which day do you want to receive the report?",
          labelposition: "left",
          required: true,
          inline: true,
          capitalizeLabel: true,
        },
      },
      numberOfWeeksWeekly: {
        "ui:widget": "number-input",
        "ui:options": {
          labelposition: "left",
        },
      },
      numberOfMonthMonthly: {
        "ui:widget": "number-input",
        "ui:options": {
          labelposition: "left",
        },
      },
      dayMonthly: {
        "ui:widget": "number-input",
        "ui:options": {
          labelposition: "left",
        },
      },
    };
  }

  render() {
    return (
      <Modal>
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onToggle}>
          <React.Fragment>
            <TooltipDropdown
              title={this.props.t("SCHEDULE_REPORT")}
              description={this.props.t("SCHEDULE_REPORT_TOOLTIP")}
              id="scheduled-report-settings-modal-tooltip"
            >
              {this.props.t("SCHEDULE_REPORT")}
            </TooltipDropdown>
          </React.Fragment>
        </ModalHeader>
        <ModalBody>
          <h4>{this.props.t("SECURITY_NOTICE")}</h4>
          <p
            className={"text-color-pumpkin-orange mb-2"}
            dangerouslySetInnerHTML={{
              __html: this.props.t("SCHEDULE_REPORT_NOTICE_LINE_1", {
                workEmail: this.props.userProfile.user.email,
              }),
            }}
          />

          <p
            className={"text-color-pumpkin-orange mb-2"}
            dangerouslySetInnerHTML={{
              __html: this.props.t("SCHEDULE_REPORT_NOTICE_LINE_2", {
                reportsExpiry: this.props.reportingParameters.reportsExpiry,
              }),
            }}
          />
          <p
            className={"text-color-pumpkin-orange mb-2"}
            dangerouslySetInnerHTML={{
              __html: this.props.t("SCHEDULE_REPORT_NOTICE_LINE_3", {
                autoDisabling: this.props.reportingParameters.autoDisabling,
              }),
            }}
          />
          <hr />
          <div className="row m-0 mt-3">
            <div className="col-md-6 pl-2">
              <label>{this.props.t("REPORT_NAME")}:</label>
            </div>
            <div className="col-md-6 pl-2 font-weight-bolder">{this.props.reportName}</div>
          </div>

          <SchemaForm
            ref={this.formRef}
            formData={this.data}
            schema={this.schema}
            uiSchema={this.uiSchema}
            onSubmit={this.onSubmit}
            externalSubmit
          />
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onToggle}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" onClick={this.submit}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapDispatchToProps = {
  toggleSaveReportModal: actionCreators.ToggleSaveReportModal,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  localize(),
  connect(
    // mapStateToProps,
    null,
    mapDispatchToProps,
  ),
)(ScheduleReportSettingsModal);
